import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import TemplateModal from './components/TemplateModal';
import DeleteTemplateModal from './components/DeleteTemplateModal';
import ActionMenu from '@/components/base/ActionMenu';
import type { ActionItem } from '@/components/base/ActionMenu';

const templateCategories = [
  { key: 'Todas', label: 'Todas', icon: 'ri-stack-line' },
  { key: 'Saudação', label: 'Saudação', icon: 'ri-hand-heart-line' },
  { key: 'Promoção', label: 'Promoção', icon: 'ri-megaphone-line' },
  { key: 'Lembrete', label: 'Lembrete', icon: 'ri-calendar-check-line' },
  { key: 'Agradecimento', label: 'Agradecimento', icon: 'ri-hearts-line' },
  { key: 'Confirmação', label: 'Confirmação', icon: 'ri-verified-badge-line' },
  { key: 'Personalizado', label: 'Personalizado', icon: 'ri-paint-brush-line' },
];

const previewExamples: Record<string, string> = {
  nome: 'João Silva',
  telefone: '(11) 98765-4321',
  email: 'joao@email.com',
  empresa: 'Acme Corp',
  cidade: 'São Paulo',
  estado: 'SP',
  data: '15/01/2025',
  hora: '14:30',
  valor: 'R$ 199,90',
  codigo: 'CUPOM25',
  link: 'https://...',
  vencimento: '20/01/2025',
  endereco: 'Av. Paulista, 1000',
  produto: 'Smartphone XYZ',
  plano: 'Premium',
};

export interface TemplateData {
  id: string;
  company_id: string;
  name: string;
  content: string;
  category: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
}

function extractVariables(content: string): string[] {
  return [...new Set(Array.from(content.matchAll(/\{\{(\w+)\}\}/g)).map((m) => m[1]))];
}

function highlightVariables(text: string): string {
  return text.replace(/\{\{(\w+)\}\}/g, '<span class="inline-block px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium mx-0.5">{{$1}}</span>');
}

function renderPreview(text: string): string {
  let result = text.replace(/\{\{(\w+)\}\}/g, (_match, varName) => {
    return previewExamples[varName] || varName.toUpperCase();
  });
  return result;
}

const categoryIconMap: Record<string, string> = {
  'Saudação': 'ri-hand-heart-line',
  'Promoção': 'ri-megaphone-line',
  'Lembrete': 'ri-calendar-check-line',
  'Agradecimento': 'ri-hearts-line',
  'Confirmação': 'ri-verified-badge-line',
  'Personalizado': 'ri-paint-brush-line',
};

const categoryColorMap: Record<string, string> = {
  'Saudação': 'bg-rose-50 text-rose-700 border-rose-200',
  'Promoção': 'bg-amber-50 text-amber-700 border-amber-200',
  'Lembrete': 'bg-sky-50 text-sky-700 border-sky-200',
  'Agradecimento': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Confirmação': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Personalizado': 'bg-violet-50 text-violet-700 border-violet-200',
};

export default function TemplatesPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id;

  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todas');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateData | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateData | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const seedingRef = useRef(false);
  const didAutoSeedRef = useRef(false);

  const fetchTemplates = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('templates')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setTemplates((data || []) as TemplateData[]);
    }
    setLoading(false);
  }, [companyId]);

  // Auto-seed default templates when page loads with no templates
  useEffect(() => {
    if (!companyId || loading || didAutoSeedRef.current || seedingRef.current) return;
    if (templates.length > 0) return;

    didAutoSeedRef.current = true;
    seedingRef.current = true;

    (async () => {
      const { data: seedData, error: seedError } = await supabase.functions.invoke('seed-default-templates', {
        body: {},
      });
      seedingRef.current = false;

      if (!seedError && seedData?.success && seedData?.created > 0) {
        const { data: freshData } = await supabase
          .from('templates')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });
        if (freshData) setTemplates(freshData as TemplateData[]);
      }
    })();
  }, [companyId, loading, templates.length]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase());
      const matchCategory = categoryFilter === 'Todas' || t.category === categoryFilter;
      const matchFavorite = !showFavoritesOnly || t.is_favorite;
      return matchSearch && matchCategory && matchFavorite;
    });
  }, [templates, search, categoryFilter, showFavoritesOnly]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    templates.forEach((t) => { counts[t.category] = (counts[t.category] ?? 0) + 1; });
    return counts;
  }, [templates]);

  const toggleFavorite = async (id: string) => {
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    const newFav = !tpl.is_favorite;
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, is_favorite: newFav } : t)));
    await supabase.from('templates').update({ is_favorite: newFav }).eq('id', id);
  };

  const handleSave = async (data: { name: string; content: string; category: string; is_favorite: boolean }) => {
    if (!companyId) return;
    setSaving(true);

    const now = new Date().toISOString();

    if (editingTemplate) {
      const { error: updErr } = await supabase
        .from('templates')
        .update({ name: data.name, content: data.content, category: data.category, is_favorite: data.is_favorite, updated_at: now })
        .eq('id', editingTemplate.id);

      if (!updErr) {
        setTemplates((prev) => prev.map((t) => (t.id === editingTemplate.id ? { ...t, ...data, updated_at: now } : t)));
      }
    } else {
      const { data: inserted, error: insErr } = await supabase
        .from('templates')
        .insert({ company_id: companyId, name: data.name, content: data.content, category: data.category, is_favorite: data.is_favorite })
        .select()
        .maybeSingle();

      if (!insErr && inserted) {
        setTemplates((prev) => [inserted as TemplateData, ...prev]);
      }
    }

    setSaving(false);
    setModalOpen(false);
    setEditingTemplate(null);
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    const { error: delErr } = await supabase.from('templates').delete().eq('id', deletingTemplate.id);
    if (!delErr) {
      setTemplates((prev) => prev.filter((t) => t.id !== deletingTemplate.id));
    }
    setDeletingTemplate(null);
    setDeleteModalOpen(false);
  };

  const openEdit = (template: TemplateData) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const copyContent = async (template: TemplateData) => {
    try {
      await navigator.clipboard.writeText(template.content);
      setCopiedId(template.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback
    }
  };

  const favCount = templates.filter((t) => t.is_favorite).length;
  const catCount = new Set(templates.map((t) => t.category)).size;
  const varCount = templates.filter((t) => extractVariables(t.content).length > 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-message-3-line text-xl text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950">Templates de Mensagem</h1>
            <p className="text-sm text-foreground-500 mt-0.5">Crie e gerencie templates com variáveis dinâmicas para suas campanhas</p>
          </div>
        </div>
        <button onClick={openCreate} disabled={saving} className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 shadow-sm hover:shadow-md">
          <i className="ri-add-line text-lg" />
          Novo template
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={fetchTemplates} className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 cursor-pointer whitespace-nowrap">Tentar novamente</button>
        </div>
      )}

      {/* Filters row */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" placeholder="Buscar por nome ou conteúdo..." />
        </div>
        <button onClick={() => { setShowFavoritesOnly(!showFavoritesOnly); setCategoryFilter('Todas'); }} className={`px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap border ${
          showFavoritesOnly
            ? 'bg-amber-50 text-amber-700 border-amber-300'
            : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50'
        }`}>
          <i className={`text-base ${showFavoritesOnly ? 'ri-star-fill' : 'ri-star-line'}`} />
          Favoritos {favCount > 0 && `(${favCount})`}
        </button>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {templateCategories.map((cat) => {
          const isActive = categoryFilter === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setCategoryFilter(cat.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                isActive
                  ? 'bg-foreground-950 text-white border-foreground-950'
                  : 'bg-white text-foreground-600 border-background-200 hover:bg-background-50 hover:border-background-300'
              }`}
            >
              <i className={`${cat.icon} text-xs`} />
              {cat.label}
              {cat.key !== 'Todas' && (categoryCounts[cat.key] ?? 0) > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-background-100'}`}>
                  {categoryCounts[cat.key]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-file-text-line text-primary-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-foreground-950 leading-none">{templates.length}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Total</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-star-fill text-amber-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-amber-600 leading-none">{favCount}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Favoritos</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-folder-line text-secondary-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-secondary-600 leading-none">{catCount}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Categorias</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-braces-line text-accent-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-accent-600 leading-none">{varCount}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">Com variáveis</p>
          </div>
        </div>
      </div>

      {/* Templates grid */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-background-200 p-16 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">Carregando templates...</span>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-background-200 p-16 text-center">
          <div className="w-20 h-20 rounded-2xl bg-background-50 flex items-center justify-center mx-auto mb-5">
            <i className="ri-message-3-line text-3xl text-foreground-300" />
          </div>
          <p className="text-base font-semibold text-foreground-700 mb-1">Nenhum template encontrado</p>
          <p className="text-sm text-foreground-500 mb-6 max-w-sm mx-auto">
            {templates.length === 0
              ? 'Crie seu primeiro template com variáveis dinâmicas para usar nas campanhas'
              : 'Tente ajustar os filtros ou buscar por outro termo'}
          </p>
          {templates.length === 0 && (
            <button onClick={openCreate} className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
              <i className="ri-add-line" /> Criar primeiro template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((template) => {
            const vars = extractVariables(template.content);
            const catIcon = categoryIconMap[template.category] || 'ri-message-3-line';
            const catColor = categoryColorMap[template.category] || 'bg-background-100 text-foreground-600 border-background-200';
            const isPreviewOpen = previewTemplate === template.id;

            return (
              <div key={template.id} className="bg-white rounded-2xl border border-background-200 overflow-hidden hover:border-background-300 transition-all group">
                {/* Card header */}
                <div className="p-5 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${catColor}`}>
                        <i className={`${catIcon} text-[10px]`} />
                        {template.category}
                      </span>
                      {template.is_favorite && (
                        <i className="ri-star-fill text-amber-500 text-sm" />
                      )}
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => copyContent(template)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Copiar conteúdo">
                        <i className={`text-base ${copiedId === template.id ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'}`} />
                      </button>
                      <button onClick={() => toggleFavorite(template.id)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-amber-500 transition-colors cursor-pointer" title={template.is_favorite ? 'Remover favorito' : 'Favoritar'}>
                        <i className={`text-base ${template.is_favorite ? 'ri-star-fill text-amber-500' : 'ri-star-line'}`} />
                      </button>
                      <ActionMenu
                        actions={[
                          { label: 'Editar', icon: 'ri-edit-line', iconColor: 'text-primary-500', onClick: () => openEdit(template) },
                          { label: 'Excluir', icon: 'ri-delete-bin-line', danger: true, onClick: () => { setDeletingTemplate(template); setDeleteModalOpen(true); } },
                        ]}
                      />
                    </div>
                  </div>
                  <h3 className="font-semibold text-foreground-950 text-sm mb-1 truncate">{template.name}</h3>

                  {/* Content preview with highlighted variables */}
                  <button
                    onClick={() => setPreviewTemplate(isPreviewOpen ? null : template.id)}
                    className="w-full text-left bg-background-50 rounded-xl p-3.5 mb-3 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {isPreviewOpen ? (
                      <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-wrap">
                        {renderPreview(template.content)}
                      </p>
                    ) : (
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2" dangerouslySetInnerHTML={{ __html: highlightVariables(template.content) }} />
                    )}
                    <p className="text-[10px] text-foreground-400 mt-2 flex items-center gap-1">
                      <i className={`text-[10px] ${isPreviewOpen ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                      {isPreviewOpen ? 'Mostrar variáveis' : 'Ver preview'}
                    </p>
                  </button>

                  {/* Variables chips */}
                  {vars.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {vars.map((variable) => (
                        <span key={variable} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-md text-[10px] font-medium border border-primary-100">
                          <i className="ri-braces-line text-[8px]" />
                          {variable}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Card footer */}
                <div className="px-5 py-2.5 bg-background-50/50 border-t border-background-100 flex items-center justify-between">
                  <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line text-[10px]" />
                    {new Date(template.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                  </span>
                  <span className="text-[10px] text-foreground-400">
                    {vars.length} {vars.length === 1 ? 'variável' : 'variáveis'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TemplateModal
        template={editingTemplate}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTemplate(null); }}
        onSave={handleSave}
        saving={saving}
      />

      <DeleteTemplateModal
        templateName={deletingTemplate?.name ?? ''}
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingTemplate(null); }}
        onConfirm={handleDelete}
      />
    </div>
  );
}